import type { Post } from '../types';
import { useState } from 'react';
import SafeIcon from './SafeIcon';
import { setupSteps } from '../store/setup';
import { useViewTime } from '../store/time';
import { accountProblem, onAccount, postTargets, aiWorks } from '../store/seed';
import AddIdeaSheet from './AddIdeaSheet';
import './calendar2.css';
import { useApp } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { postStatusHoldsDailySlot, useDemo, useWorkspaceData } from '../store/DemoStore';
import { DEMO_TODAY } from '../store/seed';
import { dictionaries } from '../i18n/locales';
import { postStatusClassName, postStatusIconName } from './postStatusDisplay';

export type CalendarView = 'week' | 'month' | 'list';
interface CalendarState {
  view: CalendarView;
  cursor: number;
  channels: string[] | null;
  campaign?: string;
  status?: string;
  account?: string;
}

const channelIconName: Record<string, string> = {
  Facebook: 'FiFacebook',
  Instagram: 'FiInstagram',
  TikTok: 'FiVideo',
  LinkedIn: 'FiLinkedin',
};
const channelBrandColor: Record<string, string> = {
  Facebook: '#1877F2',
  Instagram: '#E66B91',
  TikTok: '#DDE4F2',
  LinkedIn: '#0A66C2',
};
const MILLISECONDS_PER_DAY = 86400000;
const DAYS_PER_WEEK = 7;
const MAX_POSTS_PER_MONTH_CELL = 3;
const BASE_POSTS_PER_CHANNEL_PER_DAY = 1;
const dateOfDay = (day: number) => new Date(2026, 10, day);
const dayNumberOf = (dateValue: Date) =>
  Math.round((dateValue.getTime() - dateOfDay(0).getTime()) / MILLISECONDS_PER_DAY);
const mondayOf = (day: number) => {
  const daysSinceMonday = (dateOfDay(day).getDay() + 6) % 7;
  return day - daysSinceMonday;
};
const MOVABLE_STATUSES = [
  'Scheduled',
  'Needs your review',
  'Planned',
  'Approved · Stopped',
  'AMY is working',
];
const SELECTABLE_IN_LIST_STATUSES = ['Needs your review', 'AMY is working', 'Needs your input'];
const STATUS_FILTER_OPTIONS = [
  ['review', 'Needs your review'],
  ['scheduled', 'Scheduled'],
  ['published', 'Published'],
  ['attention', 'Needs attention'],
  ['working', 'AMY is working'],
] as const;
const isBusinessAccount = (account: { type: string }) => !/personal/i.test(account.type);

export default function CalendarPage({
  state,
  setState,
  labels,
  onReview,
  onPost,
  onSetup,
}: {
  state: CalendarState;
  setState: (next: CalendarState) => void;
  labels: Record<string, string>;
  onReview: () => void;
  onPost: (id: string) => void;
  onSetup: () => void;
}) {
  const { translate, readOnlyProps, language, formatCount } = useApp();
  const { route, role, openChat, navigate, actor, notify, canApprove } = useUi();
  const { commands } = useDemo();
  const workspaceData = useWorkspaceData(route.workspaceId);
  const { workspace, posts, accounts, settings } = workspaceData;
  const aiIsWorking = aiWorks(workspaceData.aiConnection);
  const setupStepList = setupSteps(workspaceData);
  const viewTime = useViewTime();
  const setupDoneCount = setupStepList.filter((step) => step.state === 'done').length;
  const setupStepCount = setupStepList.length;
  const [isAddIdeaOpen, setIsAddIdeaOpen] = useState(false);
  const [draggedPostId, setDraggedPostId] = useState<string | null>(null);
  const [lastMove, setLastMove] = useState<{
    id: string;
    day: number;
    time: string;
    title: string;
  } | null>(null);
  const [selectedPostIds, setSelectedPostIds] = useState<string[]>([]);
  const [isBulkApproveConfirmOpen, setIsBulkApproveConfirmOpen] = useState(false);
  const canDragToMove = (post: Post) =>
    role === 'agency' &&
    post.day > DEMO_TODAY &&
    MOVABLE_STATUSES.includes(post.status) &&
    !readOnlyProps.disabled;
  function moveFailureMessage(moveResult: string) {
    if (moveResult === 'taken')
      return translate('That slot is taken. One post per channel per day.');
    if (moveResult === 'past') return translate('Choose a day after today.');
    return translate('This post can no longer be moved.');
  }
  function dropDraggedPostOn(day: number, channel: string) {
    const post = posts.find((item) => item.id === draggedPostId);
    setDraggedPostId(null);
    if (!post || post.channel !== channel || post.day === day) return;
    const previousSlot = { id: post.id, day: post.day, time: post.time, title: post.title };
    const moveResult = commands.movePost(post.id, day, post.time, actor);
    if (moveResult === 'ok') {
      setLastMove(previousSlot);
      notify(translate('Moved “{title}” to {when}.', { title: post.title, when: dayLabel(day) }));
    } else notify(moveFailureMessage(moveResult), 'warn');
  }
  function clearFilters() {
    setState({ ...state, campaign: undefined, status: undefined, account: undefined });
  }
  const workspaceChannels = workspace.channels;
  const visibleChannels = state.channels ?? workspaceChannels;
  const clientStatusGroups: Record<string, (post: Post) => boolean> = {
    review: (post) => post.status === 'Needs your review',
    scheduled: (post) => post.status === 'Scheduled',
    published: (post) => post.status === 'Published',
    attention: (post) =>
      post.status === 'Needs your input' ||
      !!post.needsNewTime ||
      !!post.results?.some((result) => !result.succeeded) ||
      post.status === 'Approved · Stopped',
    working: (post) => post.status === 'AMY is working' || post.status === 'Planned',
  };
  const visiblePosts = posts.filter(
    (post) =>
      !['Rejected', 'Cancelled'].includes(post.status) &&
      visibleChannels.includes(post.channel) &&
      (!state.campaign || post.campaign === state.campaign) &&
      (!state.status || clientStatusGroups[state.status]?.(post)) &&
      (!state.account ||
        postTargets(post).some((name) => `${post.channel}|${name}` === state.account))
  );
  const hasActiveFilters = !!(state.campaign || state.status || state.account);
  const reviewCount = posts.filter((post) => post.status === 'Needs your review').length;
  const accountsWithProblems = accounts.filter(accountProblem);
  const firstAccountProblem = accountsWithProblems[0];
  const dateFormatter = (options: Intl.DateTimeFormatOptions) =>
    new Intl.DateTimeFormat(language, options);
  const capitalizeFirstLetter = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);
  const reviewCountPluralCategory = new Intl.PluralRules(language).select(reviewCount);
  const reviewVerb =
    dictionaries[language][`reviewVerb.${reviewCountPluralCategory}`] ??
    dictionaries[language]['reviewVerb.other'];
  const reviewText = translate('reviewCount', {
    n: reviewCount,
    posts: formatCount(reviewCount, 'post').replace(/^\d+\s/, ''),
    verb: reviewVerb,
  });

  const weekStart = mondayOf(state.cursor);
  const weekEnd = weekStart + DAYS_PER_WEEK - 1;
  const firstWeekendDay = weekStart + 5;
  const weekDays = Array.from({ length: DAYS_PER_WEEK }, (_, offset) => weekStart + offset);
  const cursorDate = dateOfDay(state.cursor);
  const firstDayOfMonth = dayNumberOf(new Date(cursorDate.getFullYear(), cursorDate.getMonth(), 1));
  const lastDayOfMonth = dayNumberOf(
    new Date(cursorDate.getFullYear(), cursorDate.getMonth() + 1, 0)
  );
  const monthGridStart = mondayOf(firstDayOfMonth);
  const monthGridWeekCount = Math.ceil((lastDayOfMonth - monthGridStart + 1) / DAYS_PER_WEEK);
  const monthGridDays = Array.from(
    { length: monthGridWeekCount * DAYS_PER_WEEK },
    (_, offset) => monthGridStart + offset
  );

  type DateRangeFormatter = Intl.DateTimeFormat & {
    formatRange: (startDate: Date, endDate: Date) => string;
  };
  const periodLabel =
    state.view === 'month'
      ? capitalizeFirstLetter(dateFormatter({ month: 'long', year: 'numeric' }).format(cursorDate))
      : (
          dateFormatter({ day: 'numeric', month: 'long', year: 'numeric' }) as DateRangeFormatter
        ).formatRange(dateOfDay(weekStart), dateOfDay(weekEnd));

  function stepPeriod(direction: -1 | 1) {
    if (state.view === 'month') {
      const firstOfSteppedMonth = new Date(
        cursorDate.getFullYear(),
        cursorDate.getMonth() + direction,
        1
      );
      setState({ ...state, cursor: dayNumberOf(firstOfSteppedMonth) });
    } else setState({ ...state, cursor: state.cursor + DAYS_PER_WEEK * direction });
  }
  function toggleChannel(channel: string) {
    const nextVisibleChannels = visibleChannels.includes(channel)
      ? visibleChannels.filter((item) => item !== channel)
      : [...visibleChannels, channel];
    const allChannelsVisible = nextVisibleChannels.length === workspaceChannels.length;
    setState({ ...state, channels: allChannelsVisible ? null : nextVisibleChannels });
  }
  const visiblePostsOnDay = (day: number) =>
    visiblePosts
      .filter((post) => post.day === day)
      .sort((firstPost, secondPost) => firstPost.time.localeCompare(secondPost.time));
  const weekPostCount = weekDays.reduce((sum, day) => sum + visiblePostsOnDay(day).length, 0);
  const weekdayLabel = (day: number) =>
    capitalizeFirstLetter(
      dateFormatter({ weekday: 'short' }).format(dateOfDay(day)).replace(/\.$/, '')
    );
  const dayLabel = (day: number) =>
    dateFormatter({ day: 'numeric', month: 'short' }).format(dateOfDay(day));
  const campaignNames = [...new Set(posts.map((post) => post.campaign).filter(Boolean))];
  const accountFilterKeys = [
    ...new Set([
      ...accounts.map((account) => `${account.network}|${account.name}`),
      ...posts.map((post) => `${post.channel}|${post.account}`),
    ]),
  ].sort();
  const hasVisibleChannels = visibleChannels.length > 0;
  const postIdsAwaitingReviewThisWeek = weekDays
    .flatMap((day) => visiblePostsOnDay(day))
    .filter((post) => post.status === 'Needs your review')
    .map((post) => post.id);
  const showSelectAllAwaitingReview =
    postIdsAwaitingReviewThisWeek.length > 1 && !readOnlyProps.disabled;
  const togglePostSelection = (postId: string) =>
    setSelectedPostIds(
      selectedPostIds.includes(postId)
        ? selectedPostIds.filter((selectedId) => selectedId !== postId)
        : [...selectedPostIds, postId]
    );
  const openWeekOf = (day: number) => setState({ ...state, view: 'week', cursor: day });
  const isSlotHeld = (channel: string, day: number, exceptPostId?: string) =>
    posts.some(
      (post) =>
        post.id !== exceptPostId &&
        post.channel === channel &&
        post.day === day &&
        postStatusHoldsDailySlot(post.status)
    );
  const isOnDisconnectedAccount = (post: Post) =>
    accounts.some((account) => onAccount(post, account) && account.health !== 'Connected');

  function postCardStatusLabel(post: Post, isDisconnected: boolean, needsAttention: boolean) {
    if (isDisconnected)
      return translate('{status} · reconnect {network}', {
        status: translate(post.status),
        network: post.channel,
      });
    if (post.status === 'AMY is working' && !aiIsWorking) return translate('Waiting for AI');
    if (post.needsNewTime) return translate('Needs a new time');
    if (post.status === 'Published' && post.results?.some((result) => result.uncertain))
      return translate('Checking result');
    if (post.status === 'Published' && post.results && needsAttention)
      return translate('Published on {ok} of {n}', {
        ok: post.results.filter((result) => result.succeeded).length,
        n: post.results.length,
      });
    return translate(post.status);
  }

  function renderPostCard(post: Post, compact = false) {
    const isDisconnected =
      ['Scheduled', 'Needs your review'].includes(post.status) && isOnDisconnectedAccount(post);
    const statusCategory = postStatusClassName(post.status);
    const needsAttention =
      post.results?.some((result) => !result.succeeded) || isDisconnected || !!post.needsNewTime;
    const statusLabel = postCardStatusLabel(post, isDisconnected, !!needsAttention);
    return (
      <button
        key={post.id}
        className={`post-card ${post.status === 'Planned' ? 'planned' : ''} ${post.day < DEMO_TODAY ? 'past' : ''} ${draggedPostId === post.id ? 'dragging' : ''}`}
        onClick={() => onPost(post.id)}
        draggable={canDragToMove(post)}
        onDragStart={(event) => {
          event.dataTransfer.setData('text/plain', post.id);
          event.dataTransfer.effectAllowed = 'move';
          setDraggedPostId(post.id);
        }}
        onDragEnd={() => setDraggedPostId(null)}
        aria-label={`${post.title} · ${post.channel} · ${dayLabel(post.day)} ${viewTime.viewerTime(post.day, post.time)} · ${statusLabel}`}
      >
        {!compact && <img src={post.image} className="post-thumb" alt="" />}
        <div className="post-meta">
          <p className="post-title">
            {post.format === 'Reel' && <SafeIcon name="FiPlay" size={12} />} {post.title}
          </p>
          <span className="post-time">
            {viewTime.viewerTime(post.day, post.time)} ·{' '}
            {post.format === 'Reel'
              ? translate('{d} video', { d: '0:24' })
              : translate(post.format)}
          </span>
          <br />
          <span className="post-channel">
            <span className="channel-brand-icon" style={{ color: channelBrandColor[post.channel] }}>
              <SafeIcon name={channelIconName[post.channel] as never} size={12} />
            </span>{' '}
            {post.channel}
          </span>
          <span className={`status ${needsAttention ? 'input' : statusCategory}`}>
            <SafeIcon
              name={(needsAttention ? 'FiAlertTriangle' : postStatusIconName(post.status)) as never}
              size={11}
            />
            {statusLabel}
          </span>
          {post.timeRequest && (
            <span className="status review">
              <SafeIcon name="FiCalendar" size={11} />
              {translate('New time suggested')}
            </span>
          )}
        </div>
      </button>
    );
  }

  function undoLastMove() {
    if (!lastMove) return;
    commands.movePost(lastMove.id, lastMove.day, lastMove.time, actor);
    setLastMove(null);
    notify(translate('Move undone'));
  }

  function renderLeftEmptyButton(post: Post, channelPrefix?: string) {
    return (
      <button key={post.id} className="free-slot-row empty-slot" onClick={() => onPost(post.id)}>
        <SafeIcon name="FiMinusCircle" size={13} />{' '}
        {channelPrefix !== undefined && <>{channelPrefix} · </>}
        {translate('Left empty on purpose')}
      </button>
    );
  }

  function renderWeekSlotContent(day: number, channel: string) {
    const channelPostsOnDay = visiblePostsOnDay(day).filter((post) => post.channel === channel);
    if (channelPostsOnDay.length === 0 && hasActiveFilters && isSlotHeld(channel, day))
      return (
        <div className="muted free-slot-row filtered-slot">
          <span className="desktop-only">◌ {translate('Taken · hidden by filters')}</span>
          <span className="mobile-only">
            {channel} · {translate('Taken · hidden by filters')}
          </span>
        </div>
      );
    if (channelPostsOnDay.length === 0)
      return (
        <div className="muted free-slot-row">
          <span className="desktop-only">
            ○ {translate(day < DEMO_TODAY ? 'Nothing posted' : 'Free slot')}
          </span>
          <span className="mobile-only">
            {channel} · {translate(day < DEMO_TODAY ? 'Nothing posted' : 'Free slot')}
          </span>
        </div>
      );
    return channelPostsOnDay.map((post) =>
      post.status === 'Intentionally empty' ? renderLeftEmptyButton(post) : renderPostCard(post)
    );
  }

  function renderListPost(post: Post) {
    if (post.status === 'Intentionally empty') return renderLeftEmptyButton(post, post.channel);
    const isSelectable =
      SELECTABLE_IN_LIST_STATUSES.includes(post.status) && !readOnlyProps.disabled;
    if (!isSelectable) return renderPostCard(post);
    return (
      <div key={post.id} className="list-select-row">
        <input
          type="checkbox"
          checked={selectedPostIds.includes(post.id)}
          onChange={() => togglePostSelection(post.id)}
          aria-label={translate('Select {post}', {
            post: `${post.title} · ${post.channel}`,
          })}
        />
        {renderPostCard(post)}
      </div>
    );
  }

  function exclusionReason(post: Post) {
    if (post.status === 'AMY is working')
      return translate('AMY is still drafting it. Review it when the draft is ready.');
    if (post.status === 'Needs your input')
      return translate('It needs material first, so there is nothing to approve yet.');
    return translate('No longer waiting for review ({status})', {
      status: translate(post.status),
    });
  }

  function renderBulkApproveDialog() {
    const selectedPosts = posts.filter((post) => selectedPostIds.includes(post.id));
    const eligible = selectedPosts.filter((post) => post.status === 'Needs your review');
    const excluded = selectedPosts.filter((post) => post.status !== 'Needs your review');
    const closeDialog = () => setIsBulkApproveConfirmOpen(false);
    const approveEligible = () => {
      const approvedCount = commands.approveMany(
        eligible.map((post) => post.id),
        actor
      );
      setSelectedPostIds([]);
      closeDialog();
      notify(translate('Approved {n} posts. They are scheduled.', { n: approvedCount }));
    };
    return (
      <div
        className="modal-backdrop"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeDialog();
        }}
      >
        <section
          className="panel modal-card bulk-confirm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-title"
        >
          <h2 id="bulk-title">
            {translate('Approve these {n} versions?', { n: eligible.length })}
          </h2>
          <ul className="bulk-list">
            {eligible.map((post) => (
              <li key={post.id}>
                <SafeIcon name={channelIconName[post.channel] as never} size={14} />
                <span>
                  <strong>{post.title}</strong> · {post.account} ({post.channel}) ·{' '}
                  {translate(post.language === 'pl' ? 'Polish' : 'English')} ·{' '}
                  {viewTime.viewerTimezoneDiffers
                    ? viewTime.viewerDateTime(post.day, post.time)
                    : `${dayLabel(post.day)} ${post.time}`}
                </span>
                {isOnDisconnectedAccount(post) && (
                  <span className="field-warn">
                    {translate(
                      '{network} needs reconnecting: approved, but it waits until reconnected',
                      { network: post.channel }
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {excluded.length > 0 && (
            <>
              <h3>{translate('Not included')}</h3>
              <ul className="bulk-list">
                {excluded.map((post) => (
                  <li key={post.id}>
                    <SafeIcon name="FiMinusCircle" size={14} />
                    <span>
                      {post.title} · {post.account} ({post.channel})
                    </span>
                    <span className="muted">{exclusionReason(post)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          <div className="button-row" style={{ marginTop: 12 }}>
            <button
              className="button primary"
              disabled={!eligible.length}
              onClick={approveEligible}
            >
              <SafeIcon name="FiCheck" />
              {translate('Approve {n}', { n: eligible.length })}
            </button>
            <button className="button" onClick={closeDialog}>
              {translate('Cancel')}
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <div className="page-title-row">
        <div>
          <h1>{labels.title}</h1>
          <div className="subtitle">
            {workspace.name} ·{' '}
            {viewTime.viewerTimezoneDiffers
              ? translate('Times in {tz}; posts go out at {posting} time', {
                  tz: viewTime.viewerTimezoneLabel,
                  posting: viewTime.postingLabel,
                })
              : translate('Times in {tz}', { tz: viewTime.viewerTimezoneLabel })}
          </div>
        </div>
        <button
          className="button primary"
          onClick={() => setIsAddIdeaOpen(true)}
          {...readOnlyProps}
        >
          <SafeIcon name="FiPlus" />
          {labels.addIdea}
        </button>
      </div>
      {workspace.id === 'nowa' && !workspaceData.welcome.done ? (
        <div className="notice-banner setup-banner" style={{ padding: '8px 16px' }}>
          <div>
            <SafeIcon name="FiPlayCircle" />{' '}
            <strong>
              {translate('Getting started: a few questions, then three sample posts in your style')}
            </strong>
          </div>
          <button className="button small primary" onClick={() => navigate({ page: 'welcome' })}>
            {translate('Continue getting started')}
            <SafeIcon name="FiArrowRight" size={14} />
          </button>
        </div>
      ) : (
        setupDoneCount < setupStepCount && (
          <div className="notice-banner setup-banner" style={{ padding: '8px 16px' }}>
            <div>
              <SafeIcon name="FiList" />{' '}
              <strong>
                {translate('Finish setting up · {n} of {total} done', {
                  n: setupDoneCount,
                  total: setupStepCount,
                })}
              </strong>
            </div>
            <button className="button small" onClick={onSetup}>
              {translate('Continue setup')}
              <SafeIcon name="FiArrowRight" size={14} />
            </button>
          </div>
        )
      )}
      {reviewCount > 0 ? (
        <div className="review-banner">
          <div>
            <strong>
              <SafeIcon name="FiAlertCircle" /> {reviewText}
            </strong>
            <div className="muted">
              {role === 'contributor' && !settings.contributorCanApprove
                ? translate('Awaiting approval from {name}', { name: workspace.owner })
                : translate('A quick review keeps your calendar moving.')}
            </div>
          </div>
          <button className="button primary" onClick={onReview}>
            {labels.startReview}
            <SafeIcon name="FiArrowRight" />
          </button>
        </div>
      ) : (
        <div className="review-banner done">
          <div>
            <strong>
              <SafeIcon name="FiCheckCircle" /> {translate('Nothing waiting for your review')}
            </strong>
            <div className="muted">
              {translate('AMY will let you know when new posts are ready.')}
            </div>
          </div>
        </div>
      )}
      <div className="summary-row">
        <div className="summary-card">
          <strong>{posts.filter((post) => post.status === 'Scheduled').length}</strong>
          <span>{labels.scheduled}</span>
        </div>
        <div className="summary-card">
          <strong>{posts.filter((post) => post.status === 'Published').length}</strong>
          <span>{labels.published}</span>
        </div>
        <div className="summary-card health-card">
          {firstAccountProblem ? (
            <>
              <strong>
                <span className="status review">
                  <SafeIcon name="FiAlertTriangle" />{' '}
                  {translate(
                    firstAccountProblem.health === 'Needs reconnecting'
                      ? '{network} needs reconnecting'
                      : '{network} is not connected',
                    { network: firstAccountProblem.network }
                  )}
                </span>
              </strong>
              <span>
                {labels.health} ·{' '}
                {role === 'contributor'
                  ? translate('{name} can reconnect it in Settings', {
                      name: firstAccountProblem.owner,
                    })
                  : translate('{ok} of {n} channels working', {
                      ok: accounts.filter(
                        (account) => isBusinessAccount(account) && account.health === 'Connected'
                      ).length,
                      n: accounts.filter(isBusinessAccount).length,
                    })}
              </span>
              {role !== 'contributor' && (
                <button
                  className="button small"
                  onClick={() => navigate({ page: 'settings' })}
                  {...readOnlyProps}
                >
                  {translate(
                    firstAccountProblem.health === 'Needs reconnecting'
                      ? 'Reconnect {network}'
                      : 'Connect {network}',
                    { network: firstAccountProblem.network }
                  )}
                  <SafeIcon name="FiArrowRight" size={14} />
                </button>
              )}
            </>
          ) : (
            <>
              <strong>
                <span className="status published">
                  <SafeIcon name="FiCheckCircle" /> {translate('All channels connected')}
                </span>
              </strong>
              <span>{labels.health}</span>
            </>
          )}
        </div>
      </div>

      <div className="calendar-toolbar">
        <div className="toolbar">
          <button
            className="icon-button"
            aria-label={translate(state.view === 'month' ? 'Previous month' : 'Previous week')}
            onClick={() => stepPeriod(-1)}
          >
            <SafeIcon name="FiChevronLeft" />
          </button>
          <strong aria-live="polite">{periodLabel}</strong>
          <button
            className="icon-button"
            aria-label={translate(state.view === 'month' ? 'Next month' : 'Next week')}
            onClick={() => stepPeriod(1)}
          >
            <SafeIcon name="FiChevronRight" />
          </button>
          <button
            className="button small"
            onClick={() => setState({ ...state, cursor: DEMO_TODAY })}
          >
            {labels.today}
          </button>
        </div>
        <div className="segment" role="group" aria-label={translate('Calendar view')}>
          {(['month', 'week', 'list'] as CalendarView[]).map((calendarView) => (
            <button
              key={calendarView}
              className={state.view === calendarView ? 'selected' : ''}
              aria-pressed={state.view === calendarView}
              onClick={() => setState({ ...state, view: calendarView })}
            >
              {labels[calendarView]}
            </button>
          ))}
        </div>
      </div>
      <div className="filter-row calendar-filters" style={{ marginBottom: 12 }}>
        {workspaceChannels.map((channel) => {
          const selected = visibleChannels.includes(channel);
          return (
            <button
              className={`channel-chip ${selected ? 'selected' : ''}`}
              key={channel}
              onClick={() => toggleChannel(channel)}
              aria-pressed={selected}
            >
              <span className="channel-brand-icon" style={{ color: channelBrandColor[channel] }}>
                <SafeIcon name={channelIconName[channel] as never} size={15} />
              </span>
              {channel}
              {selected && <SafeIcon name="FiCheck" className="channel-selected-check" size={13} />}
            </button>
          );
        })}
      </div>

      <div className="filter-row calendar-more-filters">
        <select
          className="select-field"
          aria-label={translate('Campaign')}
          value={state.campaign ?? ''}
          onChange={(event) => setState({ ...state, campaign: event.target.value || undefined })}
        >
          <option value="">{translate('All campaigns')}</option>
          {campaignNames.map((campaignName) => (
            <option key={campaignName} value={campaignName}>
              {campaignName}
            </option>
          ))}
        </select>
        <select
          className="select-field"
          aria-label={translate('Status')}
          value={state.status ?? ''}
          onChange={(event) => setState({ ...state, status: event.target.value || undefined })}
        >
          <option value="">{translate('All statuses')}</option>
          {STATUS_FILTER_OPTIONS.map(([statusGroupKey, statusGroupLabel]) => (
            <option key={statusGroupKey} value={statusGroupKey}>
              {translate(statusGroupLabel)}
            </option>
          ))}
        </select>
        <select
          className="select-field"
          aria-label={translate('Accounts')}
          value={state.account ?? ''}
          onChange={(event) => setState({ ...state, account: event.target.value || undefined })}
        >
          <option value="">{translate('All accounts')}</option>
          {accountFilterKeys.map((key) => {
            const [network, name] = key.split('|');
            const matchingAccount = accounts.find(
              (account) => account.network === network && account.name === name
            );
            const accountTypeSuffix = matchingAccount
              ? ` (${translate(matchingAccount.type)})`
              : '';
            return (
              <option key={key} value={key}>{`${name} · ${network}${accountTypeSuffix}`}</option>
            );
          })}
        </select>
        {hasActiveFilters && (
          <button className="button small ghost" onClick={clearFilters}>
            <SafeIcon name="FiX" size={14} />
            {translate('Clear filters')}
          </button>
        )}
        {hasActiveFilters && (
          <span className="muted small-print">
            {translate('Showing {n} posts that match', {
              n: visiblePosts.filter(
                (post) => (post.day >= weekStart && post.day <= weekEnd) || state.view === 'month'
              ).length,
            })}
          </span>
        )}
      </div>
      {!hasVisibleChannels && (
        <div className="panel calendar-empty">
          <SafeIcon name="FiFilter" />
          <p>{translate('All channels are hidden. Choose at least one channel above.')}</p>
        </div>
      )}

      {lastMove && (
        <div className="notice-banner calendar-undo">
          <SafeIcon name="FiCalendar" />
          <span>
            {translate('Moved “{title}”. Its approval is kept.', { title: lastMove.title })}
          </span>
          <button className="button small" onClick={undoLastMove}>
            {translate('Undo')}
          </button>
          <button
            className="icon-button small-icon"
            aria-label={translate('Close')}
            onClick={() => setLastMove(null)}
          >
            <SafeIcon name="FiX" size={14} />
          </button>
        </div>
      )}
      {hasVisibleChannels && state.view === 'week' && role === 'agency' && (
        <p className="muted small-print desktop-only calendar-drag-hint">
          {translate('Tip: drag a post to a free slot on the same channel to move it.')}
        </p>
      )}
      {hasVisibleChannels && state.view === 'week' && (
        <>
          {weekPostCount === 0 && hasActiveFilters && (
            <div className="panel calendar-empty">
              <SafeIcon name="FiFilter" />
              <div>
                <strong>{translate('No posts match these filters')}</strong>
                <p className="muted">
                  {translate('Other posts this week are hidden by the filters.')}
                </p>
              </div>
              <button className="button" onClick={clearFilters}>
                <SafeIcon name="FiX" />
                {translate('Clear filters')}
              </button>
            </div>
          )}
          {weekPostCount === 0 && !hasActiveFilters && (
            <div className="panel calendar-empty">
              <SafeIcon name="FiCalendar" />
              <div>
                <strong>{translate('Nothing planned for this week yet')}</strong>
                <p className="muted">
                  {translate(
                    'AMY plans new weeks from your campaigns. You can also ask for something specific.'
                  )}
                </p>
              </div>
              <button
                className="button"
                onClick={() =>
                  openChat(
                    translate('Please plan posts for the week of {date}.', {
                      date: dayLabel(weekStart),
                    })
                  )
                }
                {...readOnlyProps}
              >
                <SafeIcon name="FiMessageCircle" />
                {translate('Ask AMY to plan this week')}
              </button>
            </div>
          )}
          {weekPostCount > 0 && (
            <div
              className="calendar-grid"
              style={{ ['--cal-cols' as string]: visibleChannels.length }}
            >
              <div className="calendar-head">
                <div>{translate('Week of {date}', { date: dayLabel(weekStart) })}</div>
                {visibleChannels.map((channel) => (
                  <div key={channel}>
                    <SafeIcon name={channelIconName[channel] as never} size={14} /> {channel}
                  </div>
                ))}
              </div>
              {weekDays.map((day) => (
                <div
                  className={`calendar-day ${day >= firstWeekendDay ? 'weekend' : ''} ${day === DEMO_TODAY ? 'today' : ''}`}
                  key={day}
                >
                  <div className="day-label">
                    {weekdayLabel(day)}
                    <br />
                    <span className="muted">{dayLabel(day)}</span>
                    {day === DEMO_TODAY && <span className="today-tag">{labels.today}</span>}
                  </div>
                  {visibleChannels.map((channel) => {
                    const draggedPost = draggedPostId
                      ? posts.find((post) => post.id === draggedPostId)
                      : undefined;
                    const isDraggingThisChannel = !!draggedPost && draggedPost.channel === channel;
                    const canDrop =
                      !!draggedPost &&
                      isDraggingThisChannel &&
                      day > DEMO_TODAY &&
                      day !== draggedPost.day &&
                      !isSlotHeld(channel, day, draggedPost.id);
                    const dropHintClass = isDraggingThisChannel
                      ? canDrop
                        ? 'drop-ok'
                        : 'drop-no'
                      : '';
                    return (
                      <div
                        className={`slot-cell ${dropHintClass}`}
                        key={channel}
                        onDragOver={(event) => {
                          if (canDrop) {
                            event.preventDefault();
                            event.dataTransfer.dropEffect = 'move';
                          }
                        }}
                        onDrop={(event) => {
                          event.preventDefault();
                          dropDraggedPostOn(day, channel);
                        }}
                      >
                        {renderWeekSlotContent(day, channel)}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {hasVisibleChannels && state.view === 'month' && (
        <div className="month-grid" role="grid" aria-label={periodLabel}>
          {weekDays.map((_, weekdayIndex) => (
            <div className="month-weekday" key={`h${weekdayIndex}`} role="columnheader">
              {weekdayLabel(monthGridStart + weekdayIndex)}
            </div>
          ))}
          {monthGridDays.map((day) => {
            const inMonth = day >= firstDayOfMonth && day <= lastDayOfMonth;
            const postsInCell = inMonth
              ? visiblePostsOnDay(day).filter((post) => post.status !== 'Intentionally empty')
              : [];
            return (
              <div
                key={day}
                role="gridcell"
                className={`month-cell ${inMonth ? '' : 'outside'} ${day === DEMO_TODAY ? 'today' : ''}`}
              >
                <button
                  className="month-day"
                  onClick={() => openWeekOf(day)}
                  aria-label={translate('Open the week of {date}', { date: dayLabel(day) })}
                >
                  {dateOfDay(day).getDate()}
                </button>
                {postsInCell.slice(0, MAX_POSTS_PER_MONTH_CELL).map((post) => (
                  <button
                    key={post.id}
                    className={`month-chip ${postStatusClassName(post.status)}`}
                    onClick={() => onPost(post.id)}
                    title={`${post.title} · ${post.channel} · ${translate(post.status)}`}
                  >
                    <SafeIcon name={channelIconName[post.channel] as never} size={11} />
                    <span>
                      {viewTime.viewerTime(post.day, post.time)} {post.title}
                    </span>
                  </button>
                ))}
                {postsInCell.length > MAX_POSTS_PER_MONTH_CELL && (
                  <button className="month-more" onClick={() => openWeekOf(day)}>
                    {translate('+{n} more', {
                      n: postsInCell.length - MAX_POSTS_PER_MONTH_CELL,
                    })}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {hasVisibleChannels && state.view === 'list' && selectedPostIds.length > 0 && (
        <div className="bulk-bar" role="region" aria-label={translate('Selected posts')}>
          <strong>{translate('{n} selected', { n: selectedPostIds.length })}</strong>
          {canApprove ? (
            <button
              className="button primary small"
              onClick={() => setIsBulkApproveConfirmOpen(true)}
            >
              <SafeIcon name="FiCheck" size={14} />
              {translate('Approve {n}', { n: selectedPostIds.length })}
            </button>
          ) : (
            <span className="muted small-print">
              {translate('Only {name} can approve', { name: workspace.owner })}
            </span>
          )}
          <button className="button small" onClick={() => onReview()}>
            {translate('Review one by one')}
          </button>
          <button className="button small ghost" onClick={() => setSelectedPostIds([])}>
            {translate('Clear')}
          </button>
        </div>
      )}
      {hasVisibleChannels &&
        state.view === 'list' &&
        selectedPostIds.length === 0 &&
        showSelectAllAwaitingReview && (
          <button
            className="link-button bulk-select-all"
            onClick={() => setSelectedPostIds(postIdsAwaitingReviewThisWeek)}
          >
            {translate('Select all {n} waiting for review', {
              n: postIdsAwaitingReviewThisWeek.length,
            })}
          </button>
        )}
      {hasVisibleChannels && state.view === 'list' && (
        <div className="calendar-list">
          {weekPostCount === 0 &&
            (hasActiveFilters ? (
              <div className="panel calendar-empty">
                <SafeIcon name="FiFilter" />
                <div>
                  <strong>{translate('No posts match these filters')}</strong>
                  <p className="muted">
                    {translate('Other posts this week are hidden by the filters.')}
                  </p>
                </div>
                <button className="button" onClick={clearFilters}>
                  <SafeIcon name="FiX" />
                  {translate('Clear filters')}
                </button>
              </div>
            ) : (
              <div className="panel calendar-empty">
                <SafeIcon name="FiCalendar" />
                <strong>{translate('Nothing planned for this week yet')}</strong>
              </div>
            ))}
          {weekDays
            .filter((day) => visiblePostsOnDay(day).length)
            .map((day) => (
              <section key={day} className="list-day">
                <h3>
                  {capitalizeFirstLetter(
                    dateFormatter({ weekday: 'long', day: 'numeric', month: 'long' }).format(
                      dateOfDay(day)
                    )
                  )}
                  {day === DEMO_TODAY && <span className="today-tag">{labels.today}</span>}
                </h3>
                {visiblePostsOnDay(day).map(renderListPost)}
              </section>
            ))}
        </div>
      )}

      <div className="calendar-allowance-strip">
        <div className="calendar-allowance-heading">
          <strong>{translate('Daily allowance today')}:</strong>
          <span className="muted">{translate('one post per channel per day')}</span>
        </div>
        <div className="calendar-allowance-channels">
          {workspaceChannels.map((channel) => {
            const usedToday = posts.filter(
              (post) =>
                post.day === DEMO_TODAY &&
                post.channel === channel &&
                postStatusHoldsDailySlot(post.status)
            ).length;
            const blocked = accounts.some(
              (account) =>
                account.network === channel && accountProblem(account) && isBusinessAccount(account)
            );
            const dailyLimit =
              workspaceData.dailyLimitOverrides[channel] ?? BASE_POSTS_PER_CHANNEL_PER_DAY;
            return (
              <span className="calendar-allowance-chip" key={channel}>
                <span className="channel-brand-icon" style={{ color: channelBrandColor[channel] }}>
                  <SafeIcon name={channelIconName[channel] as never} size={14} />
                </span>
                <strong>{channel}:</strong>{' '}
                {blocked
                  ? translate('reconnect first')
                  : formatCount(Math.max(0, dailyLimit - usedToday), 'availability')}
              </span>
            );
          })}
        </div>
      </div>
      {isAddIdeaOpen && <AddIdeaSheet onClose={() => setIsAddIdeaOpen(false)} />}
      {isBulkApproveConfirmOpen && renderBulkApproveDialog()}
    </>
  );
}
